'use client';

import { TooltipContentSlot, TooltipHeaderSlot, TooltipTriggerSlot } from '@magicschool/spellbook/components/Tooltip/slots';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { useDirection } from '@radix-ui/react-direction';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';
import { useRef, useState } from 'react';
import { useToggleTipHandlers } from './useToggleTipHandlers';

export type TooltipMode = 'label' | 'description';
export type TooltipAriaOrder = 'first' | 'last';

export type TooltipProps = React.ComponentProps<typeof TooltipPrimitive.Root> &
  VariantProps<typeof tooltipVariants> & {
    className?: string;
    /** The preferred side of the trigger to render against when open. */
    side?: 'top' | 'right' | 'bottom' | 'left';
    /** The preferred alignment against the trigger. May change when collisions occur. */
    align?: 'start' | 'center' | 'end';
    /** Use this prop to make the tooltip open and close on click. */
    asToggleTip?: boolean;
    /** onClick event for when `asToggleTip` is true. */
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /** Semantic role: `'label'` uses `aria-labelledby`, `'description'` uses `aria-describedby`. @default 'description' */
    mode?: TooltipMode;
  };

export const tooltipVariants = cva([
  'flex',
  'flex-col',
  'gap-0.5',
  'animate-in',
  'fade-in-0',
  'zoom-in-95',
  'data-[side=bottom]:slide-in-from-top-2',
  'data-[side=left]:slide-in-from-right-2',
  'data-[side=right]:slide-in-from-left-2',
  'data-[side=top]:slide-in-from-bottom-2',
  'z-tooltip',
  'w-fit',
  'origin-(--radix-tooltip-content-transform-origin)',
  'bg-default-solid-background',
  'text-default-solid-foreground-text',
  'px-3',
  'py-2',
  'rounded-sm',
  'shadow-overlay',
  'max-w-64',
  'focus-visible:bg-accent-1-solid-background',
  /**
   * @TODO [SPE-43] Consistent UX for Truncated Text
   * https://linear.app/magicschool/issue/SPE-43/consistent-ux-for-truncated-text
   */
  'text-pretty',
  'break-words',
  'overflow-wrap',
]);

export const tooltipArrowVariants = cva([
  'z-tooltip',
  'size-2.5',
  'rounded-[2.5px]',
  '-translate-y-1.75',
  'rotate-45',
  'bg-default-solid-background',
  'fill-default-solid-background',
  'border-0',
]);

const TooltipBase: React.FC<TooltipProps> = ({
  className,
  children,
  open,
  defaultOpen,
  onOpenChange,
  onClick,
  side = 'top',
  delayDuration = 300,
  align = 'center',
  asToggleTip = false,
  mode = 'description',
  ...props
}) => {
  const dir = useDirection();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  const [internalOpen, setInternalOpen] = useState(defaultOpen || open || false);

  const {
    handleOnClick,
    handleOnKeyDown,
    handleOnPointerEnter,
    handleOnPointerLeave,
    handleOnFocus,
    handleOnPointerDown,
    handleOnBlur,
    handleOnPointerDownOutside,
    handleOnFocusContent,
    handleOnBlurContent,
  } = useToggleTipHandlers({
    onOpenChange,
    open: internalOpen,
    setOpen: setInternalOpen,
    onClick,
    triggerRef,
    contentRef,
  });

  const { slots } = useSlots(children, {
    trigger: {
      Component: TooltipTriggerSlot,
      props: {
        ref: triggerRef,
        tooltipId,
        mode,
        ...(asToggleTip && {
          onKeyDown: handleOnKeyDown,
          onClick: handleOnClick,
          onBlur: handleOnBlur,
          onFocus: handleOnFocus,
          onPointerDown: handleOnPointerDown,
          onPointerEnter: handleOnPointerEnter,
          onPointerLeave: handleOnPointerLeave,
        }),
      },
    },
    header: { Component: TooltipHeaderSlot },
    content: {
      Component: TooltipContentSlot,
    },
  });

  return (
    <>
      {slots.content && !asToggleTip && (
        <div id={tooltipId} className="sr-only">
          {slots.content}
        </div>
      )}
      <TooltipPrimitive.Root
        disableHoverableContent={false}
        delayDuration={delayDuration}
        {...props}
        open={asToggleTip ? internalOpen : open}
        onOpenChange={asToggleTip ? undefined : onOpenChange} // onOpenChange is handled internally for toggle tips, and opening behavior is controlled in trigger handler
      >
        {slots.trigger}
        {slots.content && (
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              data-spellbook-name="tooltip-content"
              sideOffset={-4}
              side={side}
              align={align}
              alignOffset={0}
              id={asToggleTip ? tooltipId : undefined}
              arrowPadding={side === 'top' || side === 'bottom' ? 20 : 4}
              className={cn(tooltipVariants(), className)}
              dir={dir}
              ref={contentRef}
              {...props}
              {...(asToggleTip && {
                onBlur: handleOnBlurContent,
                onFocus: handleOnFocusContent,
                onPointerDownOutside: handleOnPointerDownOutside,
              })}
            >
              {slots.header}
              {slots.content}
              <TooltipPrimitive.Arrow asChild width={10} height={10}>
                <div className={tooltipArrowVariants()}></div>
              </TooltipPrimitive.Arrow>
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        )}
      </TooltipPrimitive.Root>
    </>
  );
};

/**
 * Hover-triggered popup for supplemental information or accessible labels. Built on Radix UI Tooltip.
 *
 * ### When to use
 * - For supplemental information that clarifies UI elements (definitions, hints, keyboard shortcuts)
 * - For accessible labels on icon-only buttons using `mode="label"`
 * - For additional context on truncated text or abbreviated content
 * - For non-critical information users can discover on hover
 *
 * ### When NOT to use
 * - For essential information users must see: display it visibly instead
 * - For complex interactions or forms: use `Popover` or `Modal` instead
 * - For error messages or validation: use inline error states instead
 * - For menus or selectable options: use `DropdownMenu` instead
 *
 * ### API
 * - `mode`: Accessibility role: `description` (default) adds supplemental context via `aria-describedby`, `label` provides the accessible name via `aria-labelledby` (use for icon-only triggers)
 * - `side`: Preferred position relative to trigger: `top` (default), `right`, `bottom`, `left`
 * - `align`: Alignment against trigger: `start`, `center` (default), `end`
 * - `delayDuration`: Milliseconds before showing tooltip on hover. Default: `300`
 * - `asToggleTip`: When `true`, tooltip opens/closes on click instead of hover. Required for clickable content inside tooltip. It is **strongly recommended not to** put clickable elements inside a hover tooltip.
 * - `open`, `defaultOpen`, `onOpenChange`: Controlled/uncontrolled state management
 *
 * ### Composition (slots)
 * - `<Tooltip.Trigger>`: Element that triggers the tooltip (required). Use `IconButton` with `variant="tooltip"` for info icons: `<IconButton name="info" shape="circle" size="sm" variant="tooltip" />`
 * - `<Tooltip.Content>`: Tooltip text content (required). Renders as `Text variant="caption-1"`. Keep concise (1-2 lines).
 * - `<Tooltip.Header>`: Optional heading above content. Renders as `Text variant="heading-5"`. Only include when content needs a title.
 *
 * ### Accessibility
 * - `mode="description"`: Adds tooltip as supplemental info via `aria-describedby` — use when trigger already has an accessible name
 * - `mode="label"`: Makes tooltip the accessible name via `aria-labelledby` — use for icon-only buttons without labels
 * - Trigger must be an interactive element (button) for keyboard accessibility
 * - Keyboard: Focus trigger to show tooltip, Escape to dismiss
 * - Screen readers announce tooltip content based on the `mode` setting
 *
 * ### Touch devices
 * - Hover tooltips are not reliably accessible on touch devices — content appears only on long-press or focus
 * - Use `asToggleTip` for critical supplemental info that touch users need to access
 * - Consider whether the information should be visible by default on mobile layouts
 *
 * ### DropdownMenu integration
 * Use `DropdownMenu.Tooltip` to combine tooltips with dropdown triggers. See the `WithDropdownMenu` story for a complete example.
 *
 * @headless Radix Tooltip root renders no element; target `tooltip-trigger` and `tooltip-content`.
 */
export const Tooltip = Object.assign(TooltipBase, {
  Trigger: TooltipTriggerSlot,
  Content: TooltipContentSlot,
  Header: TooltipHeaderSlot,
});
